import { t } from 'i18next';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { Inventory } from '../../../common/inventory/components/Inventory';
import { InventoryRecap } from '../../../common/inventory/models/InventoryRecap';
import { JavaProvider } from '../../../common/reports/models/JavaProvider';
import { ObjectStat } from '../../../common/reports/models/ObjectStat';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { PATHS } from '../../../routing/routes';
import { ReportCallLimiting } from '../../common/components/ReportCallLimiting';
import { ReportDetails } from '../../common/components/ReportDetails';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { Report, ReportDone, ReportFailed, ReportPending } from '../../common/models/Report';
import { BlankSummary } from '../components/BlankSummary';
import { JavaProvidersTable } from '../components/JavaProvidersTable';
import { ObjectStatsTable } from '../components/ObjectStatsTable';
import { ReportSummaryRepository } from '../services/ReportSummaryRepository';

type Props = WithRouterProps<ReportOutletContextType> & {};

type State = BaseState & {
  initialLoad: boolean;
  loadingData: boolean;
};

class Controller extends BaseController<Props, State> {
  private readonly reportSummaryRepository = new ReportSummaryRepository();

  private report: Report | null;
  private inventory: InventoryRecap | null;
  private javaProviders: JavaProvider[] | null;
  private objectStats: ObjectStat[] | null;

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.report = null;
    this.inventory = null;
    this.javaProviders = null;
    this.objectStats = null;

    this.state = { ...super.getBaseState(), initialLoad: true, loadingData: true };
  }

  componentDidMount(): void {
    super.componentDidMount();

    this.loadReportContext();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    this.onReportContextChanged(prevProps.context.report, this.props.context.report);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.reportSummaryRepository.cancelAllRequests();
  }

  private async fetchSummary() {
    const { report } = this.props.context;

    if (!(report instanceof ReportDone)) return;

    try {
      const { inventory, javaProviders, objectStats } = await this.reportSummaryRepository.getSummary(report.getId());
      this.report = report;
      this.inventory = inventory;
      this.javaProviders = javaProviders;
      this.objectStats = objectStats;
      this.setState({ initialLoad: false, loadingData: false });
    } catch (e: unknown) {
      this.handleError(e);
      return null;
    }
  }

  render() {
    const { initialLoad, loadingData } = this.state;

    const nothingToDisplay =
      !initialLoad &&
      !loadingData &&
      this.report instanceof ReportDone &&
      !(this.inventory && !this.inventory.isEmpty()) &&
      !(this.javaProviders && this.javaProviders.length > 0) &&
      !(this.objectStats && this.objectStats.length > 0) &&
      !this.report.getCallLimiting();

    const callLimiting = this.report instanceof ReportDone ? this.report.getCallLimiting() : null;

    return this.renderBase(
      <>
        <ReportDetails
          report={this.report}
          loading={!initialLoad && loadingData}
          initialLoad={!this.report}
          versionLink={(reportId: number) => UrlHelper.generatePath(PATHS.REPORT_INVENTORY, { reportId: reportId.toString() })}
          compareLink={(r) => UrlHelper.generatePath(PATHS.REPORT_DIFF, { reportId: r.getId().toString() })}
          profileLink={(r) => UrlHelper.generatePath(PATHS.PROFILE_DETAIL, { profileId: r.getProfile()?.getId()?.toString() || '' })}
        />
        {nothingToDisplay ? (
          <BlankSummary />
        ) : (
          <>
            <Inventory inventory={this.inventory} loadingData={loadingData} initialLoad={initialLoad} />
            {this.javaProviders && this.javaProviders.length > 0 && (
              <JavaProvidersTable javaProviders={this.javaProviders} parentLoading={initialLoad || loadingData} />
            )}
            <ObjectStatsTable title={t('statistics')} stats={this.objectStats} loading={loadingData} initialLoad={initialLoad} />
            <ReportCallLimiting limits={callLimiting} />
          </>
        )}
      </>
    );
  }

  private loadReportContext() {
    // When the component mounts, consider that the previous report is null
    this.onReportContextChanged(null, this.props.context.report);
  }

  private onReportContextChanged(prevReport: Report | null, currReport: Report | null) {
    // Report hasn't changed, do nothing
    if (prevReport === currReport) return;

    if (prevReport !== null && prevReport.getId() !== currReport?.getId()) {
      // Report has changed (e.g. through a version selector)
      // Reset the internal data and reload
      this.setState({ loadingData: true }, () => this.loadReportContext());
      return;
    }

    if (!currReport || currReport instanceof ReportPending) {
      // The report is not ready yet, display the initial loading state
      this.report = currReport;
      this.setState({ loadingData: true });
    } else if (currReport instanceof ReportFailed) {
      this.report = currReport;
      this.inventory = null;
      this.javaProviders = null;
      this.objectStats = null;
      // The report is failed, display the blank loaded state
      this.setState({ initialLoad: false, loadingData: false });
    } else if (currReport instanceof ReportDone) {
      // The report is done, load the summary
      this.fetchSummary();
    }
  }
}

export const ReportSummaryController = withRouter<ReportOutletContextType>(Controller);
